// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2025 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import { ButtonPresentationProps } from "@eisinc/MobiusOverrides/Button";
import { TextFieldPresentationProps } from "@eisinc/MobiusOverrides/TextField";
import ProductAddToCartButton from "@eisinc/Overrides/Components/ProductAddToCartButton";
import ProductAddToListLink, { ProductAddToListLinkStyles } from "@eisinc/Overrides/Components/ProductAddToListLink";
import { ProductAvailabilityStyles } from "@eisinc/Overrides/Components/ProductAvailability";
import ProductDescription, { ProductDescriptionStyles } from "@eisinc/Overrides/Components/ProductDescription";
import { ProductPartNumbersStyles } from "@eisinc/Overrides/Components/ProductPartNumbers";
import ProductPartNumbers from "@insite/content-library/Components/ProductPartNumbers";

import ProductPrice, { ProductPriceStyles } from "@eisinc/Overrides/Components/ProductPrice";
import ProductQuantityOrdered from "@eisinc/Overrides/Components/ProductQuantityOrdered";
import mergeToNew from "@insite/client-framework/Common/mergeToNew";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { ProductContext, ProductContextModel } from "@insite/client-framework/Components/ProductContext";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import updateProductInfo from "@insite/client-framework/Store/Components/ProductInfoList/Handlers/UpdateProductInfo";
import { getProductInfoFromList } from "@insite/client-framework/Store/Components/ProductInfoList/ProductInfoListSelectors";
import { getSettingsCollection } from "@insite/client-framework/Store/Context/ContextSelectors";
import { ProductModel } from "@insite/client-framework/Types/ApiModels";
import { ProductBrandStyles } from "@insite/content-library/Components/ProductBrand";
import ProductContextAvailability from "@insite/content-library/Components/ProductContextAvailability";
import ProductImage, { ProductImageStyles } from "@insite/content-library/Components/ProductImage";
import ProductQuantityBreakPricing, {
    ProductQuantityBreakPricingStyles,
} from "@insite/content-library/Components/ProductQuantityBreakPricing";
import ProductUnitOfMeasureSelect from "@insite/content-library/Components/ProductUnitOfMeasureSelect";
import { GridContainerProps } from "@insite/mobius/GridContainer";
import GridItem, { GridItemProps } from "@insite/mobius/GridItem";

import ProductBrand from "@eisinc/Overrides/Components/ProductBrand";
import { BaseTheme } from "@insite/mobius/globals/baseTheme";
import { breakpointMediaQueries } from "@insite/mobius/utilities";
import getColor from "@insite/mobius/utilities/getColor";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import React from "react";
import { connect, ResolveThunks } from "react-redux";
import { css } from "styled-components";

interface OwnProps {
    product: ProductModel;
    widgetId: string;
    showBrand: boolean;
    showPartNumbers: boolean;
    showPrice: boolean;
    showAvailability: boolean;
    showAddToCart: boolean;
    showAddToList: boolean;
    extendedStyles?: PurchasedProductCardStyles;
}

const mapStateToProps = (state: ApplicationState, ownProps: OwnProps) => ({
    productSettings: getSettingsCollection(state).productSettings,
    productInfo: getProductInfoFromList(state, ownProps.widgetId, ownProps.product.id),
});

const mapDispatchToProps = {
    updateProductInfo,
};

type Props = OwnProps & ReturnType<typeof mapStateToProps> & ResolveThunks<typeof mapDispatchToProps>;

export interface PurchasedProductCardStyles {
    container?: GridContainerProps;
    leftColumnGridItem?: GridItemProps;
    productImage?: ProductImageStyles;
    rightColumnGridItem?: GridItemProps;
    rightColumnGridContainer?: GridContainerProps;
    infoGridItem?: GridItemProps;
    infoContainer?: GridContainerProps;
    brandStyles?: ProductBrandStyles;
    descriptionStyles?: ProductDescriptionStyles;
    brandGridItem?: GridItemProps;
    descriptionGridItem?: GridItemProps;
    partNumbersGridItem?: GridItemProps;
    partNumbersStyles?: ProductPartNumbersStyles;
    availabilityGridItem?: GridItemProps;
    availabilityStyles?: ProductAvailabilityStyles;
    priceGridItem?: GridItemProps;
    priceStyles?: ProductPriceStyles;
    quantityBreakPricing?: ProductQuantityBreakPricingStyles;
    actionsGridItem?: GridItemProps;
    actionsContainer?: GridContainerProps;
    quantityGridItem?: GridItemProps;
    quantityOrdered?: TextFieldPresentationProps;
    addToGridItem?: GridItemProps;
    addToCartButton?: ButtonPresentationProps;
    addToListWrapper?: InjectableCss;
    addToListLink?: ProductAddToListLinkStyles;
    // XNG-Change
    imageStyledWrapper?: InjectableCss;
    addToCartWrapper?: InjectableCss;
    mainWrapper?: InjectableCss;
}

export const purchasedProductCardStyles: PurchasedProductCardStyles = {
    container: {
        gap: 0,
        css: css`
            padding: 10px 0;
            border-bottom: 1px solid ${getColor("common.border")};
        `,
    },
    leftColumnGridItem: {
        width: [4, 4, 2, 1, 1],
        css: css`
            padding-right: 15px;
        `,
    },
    rightColumnGridItem: {
        width: [8, 8, 10, 11, 11],
    },
    rightColumnGridContainer: { gap: 0 },
    infoGridItem: {
        width: [12, 12, 5, 6, 6],
    },
    infoContainer: {
        gap: 0,
        css: css`
            width: 100%;
        `,
    },
    descriptionStyles: {
        productDetailLink: {
            css: css`
                width: 100%;
            `,
            typographyProps: {
                size: 14,
            },
        },
    },
    brandGridItem: {
        width: 12,
    },
    descriptionGridItem: {
        width: 12,
    },
    partNumbersGridItem: {
        width: 12,
    },
    partNumbersStyles: {
        erpNumberLabelText: {
            weight: "normal",
            color: "text.disabled",
        },
        erpNumberValueText: {
            color: "text.disabled",
            css: css`
                padding-left: 5px;
            `,
        },
    },
    availabilityGridItem: {
        width: 12,
    },
    priceGridItem: {
        width: [12, 12, 3, 3, 3],
        css: css`
            flex-direction: column;
            padding-right: 20px;
        `,
    },
    priceStyles: {
        wrapper: {
            css: css`
                display: flex;
                flex-direction: column;
                width: auto;
                text-align: end;
                ${({ theme }: { theme: BaseTheme }) =>
                    breakpointMediaQueries(
                        theme,
                        [
                            null,
                            null,
                            css`
                                align-self: flex-end;
                            `,
                        ],
                        "min",
                    )}
            `,
        },
    },
    quantityBreakPricing: {
        viewLink: {
            typographyProps: { size: 12 },
            css: css`
                text-align: end;
                ${({ theme }: { theme: BaseTheme }) =>
                    breakpointMediaQueries(
                        theme,
                        [
                            null,
                            null,
                            css`
                                align-self: flex-end;
                            `,
                        ],
                        "min",
                    )}
            `,
        },
    },
    actionsGridItem: {
        width: [12, 12, 4, 3, 3],
    },
    actionsContainer: {
        gap: 0,
        css: css`
            width: 100%;
        `,
    },
    quantityGridItem: { width: 3 },
    addToGridItem: {
        width: 9,
        css: css`
            flex-direction: column;
            align-items: center;
            padding-left: 10px;
        `,
    },
    addToCartButton: {
        css: css`
            width: 100%;
        `,
    },
    addToListWrapper: {
        css: css`
            margin-top: 5px;
        `,
    },
    addToListLink: {
        link: {
            typographyProps: { size: 13 },
        },
    },
};

const PurchasedProductCard: React.FC<Props> = ({
    product,
    productInfo,
    showBrand,
    showPartNumbers,
    showPrice,
    showAvailability,
    showAddToCart,
    showAddToList,
    productSettings,
    extendedStyles,
    updateProductInfo,
    widgetId,
}) => {
    const [styles] = React.useState(() => mergeToNew(purchasedProductCardStyles, extendedStyles));

    if (!productInfo || !product) {
        return null;
    }

    const productContext: ProductContextModel = {
        product,
        productInfo,
        onUnitOfMeasureChanged: unitOfMeasure => {
            updateProductInfo({ id: widgetId, productId: product.id, unitOfMeasure });
        },
        onQtyOrderedChanged: qtyOrdered => {
            updateProductInfo({ id: widgetId, productId: product.id, qtyOrdered });
        },
    };
    return (
        <ProductContext.Provider value={productContext} data-test-selector="widget_PurchasedProducts">
            {/* xng-change - using article for the wrapper to make it more semantic */}
            <StyledWrapper
                as="article"
                {...styles.mainWrapper}
                data-test-selector={`purchasedProductCard_${product.id}`}
            >
                <StyledWrapper {...styles.imageStyledWrapper}>
                    <ProductImage
                        {...{ widgetId }}
                        extendedStyles={styles.productImage}
                        product={productContext}
                        showSmallImage={true}
                    />
                </StyledWrapper>

                <StyledWrapper {...styles.infoContainer}>
                    <GridItem {...styles.descriptionGridItem}>
                        <ProductDescription
                            {...{ widgetId }}
                            product={productContext}
                            extendedStyles={styles.descriptionStyles}
                        />
                    </GridItem>
                    {showPartNumbers && (
                        <StyledWrapper {...styles.partNumbersGridItem}>
                            <ProductPartNumbers
                                customerProductNumber={product.customerProductNumber}
                                manufacturerItem={product.manufacturerItem}
                                productNumber={
                                    !product.customerProductNumber ||
                                    !product.properties["specNumber"] ||
                                    !product.manufacturerItem
                                        ? product.productNumber
                                        : ""
                                }
                                showLabel={true}
                                extendedStyles={styles.partNumbersStyles}
                                {...({
                                    modelNumber: product.detail?.modelNumber || "",
                                    specNumber: product.properties.specNumber || "",
                                } as any)}
                            />
                        </StyledWrapper>
                    )}
                    {product.brand && showBrand && (
                        <ProductBrand showLabel={true} brand={product.brand} extendedStyles={styles.brandStyles} />
                    )}
                </StyledWrapper>
                <StyledWrapper {...styles.priceGridItem}>
                    {showPrice && (
                        <>
                            <ProductPrice
                                product={productContext}
                                showLabel={false}
                                showSavings={true}
                                showSavingsAmount={productSettings.showSavingsAmount}
                                showSavingsPercent={productSettings.showSavingsPercent}
                                extendedStyles={styles.priceStyles}
                            />
                            <ProductQuantityBreakPricing extendedStyles={styles.quantityBreakPricing} />
                            {product.unitOfMeasures && (
                                <ProductUnitOfMeasureSelect labelOverride="" id={`${product.id}-uom`} />
                            )}
                        </>
                    )}
                </StyledWrapper>
                <StyledWrapper {...styles.availabilityGridItem}>
                    {productSettings.showInventoryAvailability && showAvailability && (
                        <ProductContextAvailability extendedStyles={styles.availabilityStyles} />
                    )}
                </StyledWrapper>
                <StyledWrapper {...styles.actionsGridItem}>
                    {showAddToCart && (
                        <StyledWrapper {...styles.addToCartWrapper}>
                            <ProductQuantityOrdered labelOverride="" extendedStyles={styles.quantityOrdered} />
                            <ProductAddToCartButton
                                data-test-selector={`actionsAddToCart${product.id}`}
                                extendedStyles={styles.addToCartButton}
                                iconOnly={true}
                            />
                        </StyledWrapper>
                    )}
                    {showAddToList && (
                        <StyledWrapper {...styles.addToListWrapper}>
                            <ProductAddToListLink
                                data-test-selector={`actionsAddToList${product.id}`}
                                extendedStyles={styles.addToListLink}
                                showIcon={true}
                            />
                        </StyledWrapper>
                    )}
                </StyledWrapper>
            </StyledWrapper>
        </ProductContext.Provider>
    );
};

export default connect(mapStateToProps, mapDispatchToProps)(PurchasedProductCard);
